import type { ProgressResponse, TrackStructureFull } from '@devlearn/shared';
import { Link } from 'react-router';
import { CheckIcon, LockIcon } from '@/components/icons';
import { challengePath } from '@/features/content';
import { cn } from '@/lib/cn';

type SkillPathProps = { track: TrackStructureFull; progress: ProgressResponse };

type NodeState = 'complete' | 'current' | 'open' | 'locked';

/** Six chapter nodes: complete (check), current (accent, "IN PROGRESS"), locked (dashed, lock). */
export function SkillPath({ track, progress }: SkillPathProps) {
  const currentChapterId = progress.currentChallenge
    ? track.chapters.find((c) => c.slug === progress.currentChallenge!.chapterSlug)?.id
    : undefined;

  const nodes = track.chapters.map((chapter) => {
    const p = progress.chapters.find((c) => c.id === chapter.id);
    const state: NodeState =
      p?.state === 'completed'
        ? 'complete'
        : p?.state === 'locked' || !p
          ? 'locked'
          : chapter.id === currentChapterId
            ? 'current'
            : 'open';
    const first =
      chapter.challenges.find((c) => progress.challenges.find((x) => x.id === c.id)?.state !== 'completed') ??
      chapter.challenges[0]!;
    return { chapter, p, state, href: challengePath({ track, chapter, challenge: first }) };
  });

  return (
    <section
      aria-labelledby="path-h"
      className="flex flex-col gap-4 md:gap-6 md:rounded-xl md:border md:border-line md:bg-surface md:p-7"
    >
      <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between">
        <h2
          id="path-h"
          className="m-0 text-meta font-semibold uppercase tracking-[0.04em] text-ink-muted md:text-xl md:normal-case md:tracking-normal md:text-ink"
        >
          {track.title}
        </h2>
        <span className="hidden text-sm text-ink-muted md:block">
          Beat each chapter's boss project to unlock the next
        </span>
      </div>
      <ol className="m-0 grid list-none grid-cols-1 gap-2 p-0 md:grid-cols-3 md:gap-3 xl:grid-cols-6">
        {nodes.map(({ chapter, p, state, href }) => {
          const number = String(chapter.position).padStart(2, '0');
          const count = p ? `${p.completed} / ${p.total}` : `0 / ${chapter.challenges.length}`;
          const sub =
            state === 'complete'
              ? `${count} · boss beaten`
              : state === 'locked'
                ? `${chapter.challenges.length} challenges`
                : `${count} challenges`;
          const body = (
            <>
              <span className="flex items-center justify-between max-md:hidden">
                <span
                  className={cn(
                    'font-mono text-xs',
                    state === 'current' ? 'text-accent-hover' : 'text-ink-muted',
                  )}
                >
                  {number}
                </span>
                {state === 'complete' && <CheckIcon label="Complete" className="text-success" />}
                {state === 'current' && (
                  <span className="text-[11px] font-semibold text-accent-hover">IN PROGRESS</span>
                )}
                {state === 'locked' && <LockIcon label="Locked" />}
              </span>
              <span className="flex items-center gap-3 md:hidden">
                {state === 'complete' && <CheckIcon label="Complete" className="text-success" />}
                {state === 'current' && (
                  <span aria-hidden="true" className="size-[18px] rounded-full border-2 border-accent" />
                )}
                {state === 'open' && (
                  <span aria-hidden="true" className="size-[18px] rounded-full border-2 border-line-strong" />
                )}
                {state === 'locked' && <LockIcon label="Locked" />}
              </span>
              <span className="flex-1 text-body-app font-semibold max-md:font-medium">{chapter.title}</span>
              <span
                className={cn(
                  'text-meta max-md:font-mono max-md:text-xs',
                  state === 'current' ? 'text-accent-hover' : state === 'locked' ? '' : 'text-ink-muted',
                )}
              >
                <span className="md:hidden">{count.replace(' / ', '/')}</span>
                <span className="max-md:hidden">{sub}</span>
              </span>
            </>
          );
          const base =
            'flex min-h-[52px] rounded-lg p-4 no-underline max-md:items-center max-md:gap-3 max-md:py-0 md:flex-col md:gap-2';
          const tone =
            state === 'current'
              ? 'border-2 border-accent bg-accent-soft text-ink'
              : state === 'locked'
                ? 'border border-dashed border-line-strong text-ink-subtle'
                : 'border border-line bg-surface text-ink md:bg-canvas hover:border-ink';
          return (
            <li key={chapter.id} aria-current={state === 'current' ? 'step' : undefined}>
              {state === 'locked' ? (
                <div className={cn(base, tone)}>{body}</div>
              ) : (
                <Link to={href} className={cn(base, tone, 'hover:text-ink')}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
